// Lean lookup — the full `currencyByCode` records (names, flags, …) are
// picker data; importing them shipped the whole ~28KB dataset to every
// storefront visitor for a symbol + decimal-digit lookup.
import { CURRENCY_FORMAT_DATA } from "./currency-format-data";

/** Operator-configurable currency display settings for an agency.
 *  Amounts are NEVER converted — this only controls how a stored
 *  minor-unit amount is rendered in the agency's chosen currency. */
export interface CurrencyFormatSettings {
  /** ISO 4217 code, uppercase (e.g. "USD"). */
  currencyCode: string;
  /** Token template, e.g. "{currency_symbol}{amount}". */
  displayFormat: string;
  /** Character(s) between the integer and fractional parts, e.g. ".". */
  decimalSeparator: string;
  /** Character(s) grouping thousands, e.g. ","; empty string = no grouping. */
  thousandSeparator: string;
}

/** Format defaults applied when an agency has not configured currency display. */
export const DEFAULT_CURRENCY_FORMAT = {
  currencyCode: "USD",
  displayFormat: "{currency_symbol}{amount}",
  decimalSeparator: ".",
  thousandSeparator: ",",
} as const satisfies CurrencyFormatSettings;

/** Group the integer part into thousands using `sep` (no-op when `sep` is ""). */
export function groupThousands(intDigits: string, sep: string): string {
  if (sep === "") return intDigits;
  return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}

/** Symbol + decimal-place count for a currency code, with the same defaults
 *  `formatMoney` applies to an unknown code. Shared with the compact
 *  formatter so the two can never disagree on a currency's shape. */
export function currencyMeta(currencyCode: string): {
  code: string;
  symbol: string;
  decimals: number;
} {
  const code = currencyCode.toUpperCase();
  const meta = CURRENCY_FORMAT_DATA[code];
  return { code, symbol: meta?.[0] ?? code, decimals: meta?.[1] ?? 2 };
}

/** Arabic, Hebrew and Thaana ranges — the scripts currency symbols in this
 *  dataset actually use (KWD "د.ك", BHD ".د.ب", AED "د.إ", ILS "₪"). */
const RTL_CHARS = /[֐-׿؀-ۿ܀-ݏހ-޿]/;

/** Bidi-isolate an RTL currency symbol so it cannot reorder the digits beside
 *  it.
 *
 *  Without this, "د.ك4.55K" renders as "4.55د.كK": the Unicode bidi algorithm
 *  pulls European digits adjacent to an RTL run into that run's direction,
 *  which moves the number to the symbol's left and strands any trailing
 *  compact suffix on its own. FSI…PDI (U+2068…U+2069) makes the symbol its own
 *  run so the amount stays put.
 *
 *  Applied ONLY when the symbol contains RTL characters, so "$", "€" and every
 *  other Latin-script symbol keep byte-identical output. */
function isolateIfRtl(symbol: string): string {
  return RTL_CHARS.test(symbol) ? `⁨${symbol}⁩` : symbol;
}

/** Substitute a rendered amount into the agency's display template. Extracted
 *  so `formatMoneyApprox` renders through the exact same template path — an
 *  agency using "{amount} {currency_code}" must get that layout whether or not
 *  the value was compacted. */
export function applyCurrencyTemplate(
  amount: string,
  displayFormat: string,
  { code, symbol }: { code: string; symbol: string },
): string {
  return displayFormat
    .replaceAll("{currency_symbol}", isolateIfRtl(symbol))
    .replaceAll("{currency_code}", code)
    .replaceAll("{amount}", amount);
}

/**
 * Render an integer minor-unit amount in the agency's currency + format.
 *
 * Pure and deterministic — no `Intl`, no locale, no exchange rate. Decimal
 * precision comes from the currency dataset (e.g. JPY=0, USD=2, BHD=3).
 *
 *   formatMoney(123456, { currencyCode: "USD", displayFormat: "{currency_symbol}{amount}",
 *                         decimalSeparator: ".", thousandSeparator: "," })  // "$1,234.56"
 */
export function formatMoney(
  amountMinor: number,
  settings: CurrencyFormatSettings,
): string {
  return render(amountMinor, settings, "drop-whole-decimals");
}

/** {@link formatMoney} with {@link DEFAULT_CURRENCY_FORMAT}, for a surface
 *  that has no agency format settings to hand. The code may be any case. */
export function formatMoneyDefault(
  amountMinor: number,
  currencyCode: string,
): string {
  return formatMoney(amountMinor, {
    ...DEFAULT_CURRENCY_FORMAT,
    currencyCode: currencyCode.toUpperCase(),
  });
}

/**
 * The same rendering, except a whole amount KEEPS its decimals: "$780.00",
 * "﷼78.000". For documents rather than display.
 *
 * A receipt, an invoice and a booking sheet state an amount someone may
 * reconcile against a bank line or file for reimbursement, and those are
 * written to the currency's full precision by convention. The storefront has
 * the opposite need — a price is read at a glance and ".00" is noise in every
 * one of them — which is why this is two named functions rather than one with
 * a flag.
 *
 * It also keeps the booking PDF in step with the confirmation EMAIL for the
 * same booking. Emails do not use this module at all (see
 * `workers/.../format-email-money.ts`, which renders through `Intl` because an
 * outbox payload carries no agency separators), and `Intl` always writes the
 * currency's decimals. Without this, one booking arrived as "$780.00" by email
 * and "$780" on its PDF.
 */
export function formatMoneyExact(
  amountMinor: number,
  settings: CurrencyFormatSettings,
): string {
  return render(amountMinor, settings, "keep-whole-decimals");
}

function render(
  amountMinor: number,
  settings: CurrencyFormatSettings,
  wholeAmounts: "drop-whole-decimals" | "keep-whole-decimals",
): string {
  if (!Number.isFinite(amountMinor)) return "";

  const { code, symbol, decimals } = currencyMeta(settings.currencyCode);

  const negative = amountMinor < 0;
  const abs = Math.abs(amountMinor);
  const major = abs / 10 ** decimals;

  const fixed = major.toFixed(decimals);
  const dot = fixed.indexOf(".");
  const intPart = dot === -1 ? fixed : fixed.slice(0, dot);
  const fracPart = dot === -1 ? "" : fixed.slice(dot + 1);
  const grouped = groupThousands(intPart, settings.thousandSeparator);
  // A whole amount drops its decimals: "$17,683", not "$17,683.00". The zeroes
  // carry no information a reader needs and cost width in every price on the
  // storefront. A fractional amount keeps every place the currency defines, so
  // $17,683.50 is unchanged and no precision is ever rounded away.
  const isWhole = /^0*$/.test(fracPart);
  const dropDecimals = wholeAmounts === "drop-whole-decimals" && isWhole;
  const amount =
    decimals > 0 && fracPart !== "" && !dropDecimals
      ? `${grouped}${settings.decimalSeparator}${fracPart}`
      : grouped;

  const rendered = applyCurrencyTemplate(amount, settings.displayFormat, {
    code,
    symbol,
  });

  // Sign prefixes the whole rendered value (e.g. "-$500.00", "-500,00 €").
  return negative ? `-${rendered}` : rendered;
}
